/**
 * client/net/events.ts — server events scheduled on the render timeline [NET §6.5, pitfall 8].
 * OWNER: NETCODE agent.
 *
 * Every event that arrives in a snapshot is played when renderTime reaches that snapshot's
 * serverTime — the moment the snapshot becomes the "current" one for interpolation (Q3
 * CG_TransitionSnapshot fires entity events then). So a remote shot is heard exactly when the
 * shooter's model is drawn firing, not RTT/2 + 50 ms earlier on arrival.
 * Own predicted events never arrive here: the server forwards ENT_EVENTs only to OTHER clients and
 * the local ones are played (and deduped by eventSequence) by the predictor.
 * Sounds that come due far too late (tab was hidden, clock reset) are dropped instead of played as
 * a burst; game events (kill feed, score, match state) are always delivered.
 */
import { GameEventType, type GameEvent } from '@shared/types';
import type { FrameEvent } from '../types';

/** ESTIMATE: entity events (sounds/fx) more than this late are dropped. */
export const STALE_ENT_EVENT_MS = 500;
/** Hard cap on queued events (memory guard). */
const MAX_QUEUED = 4096;

interface Scheduled { at: number; e: FrameEvent }

export class EventScheduler {
  private q: Scheduled[] = [];
  dropped = 0;

  /** Queue a snapshot's events, to play at `at` (the snapshot's serverTime). */
  pushSnapshot(at: number, events: readonly GameEvent[]): void {
    for (const ge of events) {
      const e: FrameEvent = ge.type === GameEventType.ENT_EVENT
        ? { src: 'ent', ent: ge.ent, ev: ge.ev, parm: ge.parm }
        : { src: 'game', ge };
      this.q.push({ at, e });
    }
    if (this.q.length > MAX_QUEUED) { this.dropped += this.q.length - MAX_QUEUED; this.q.splice(0, this.q.length - MAX_QUEUED); }
  }

  /** Move every event due at renderTime into `out` (in order). */
  drain(renderTime: number, out: FrameEvent[]): void {
    let i = 0;
    for (; i < this.q.length; i++) {
      const s = this.q[i];
      if (s.at > renderTime) break;
      if (s.e.src === 'ent' && renderTime - s.at > STALE_ENT_EVENT_MS) { this.dropped++; continue; }
      out.push(s.e);
    }
    if (i) this.q.splice(0, i);
  }

  clear(): void { this.q.length = 0; }
  get size(): number { return this.q.length; }
}
